import { MultiSelect, Select } from '@mantine/core';
import { useState } from 'react';
import type { ReactElement } from 'react';
import type { RuleClaimFieldOption } from './ruleClaimFieldOptions';
import {
  getRuleClaimOptionValue,
  getRuleClaimSelectData,
  getRuleClaimSelectKeys,
  getRuleClaimValuesFromKeys,
} from './ruleClaimFieldOptions';

interface RuleClaimSelectProps {
  label?: string;
  placeholder?: string;
  options: RuleClaimFieldOption[];
  testID: string;
}

type RuleClaimValueSelectProps = RuleClaimSelectProps &
  (
    | { multiple?: false; value: string; onChange: (value: string) => void }
    | { multiple: true; value: string[]; onChange: (value: string[]) => void }
  );

/** Shares provincial option keys and saved-code handling across single and multiple selections. */
export const RuleClaimValueSelect = (
  props: RuleClaimValueSelectProps
): ReactElement => {
  const options =
    props.multiple === true
      ? props.options.filter((option) => getRuleClaimOptionValue(option).trim())
      : props.options;
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const values = props.multiple === true ? props.value : [props.value];
  const selectedValues = getRuleClaimSelectKeys(options, values, selectedKeys);
  const updateSelection = (keys: string[]): string[] => {
    setSelectedKeys(keys);
    return getRuleClaimValuesFromKeys(options, keys);
  };
  const sharedProps = {
    label: props.label,
    placeholder: props.placeholder,
    data: getRuleClaimSelectData(options, values),
    searchable: true,
    clearable: true,
    'data-testid': props.testID,
  };

  if (props.multiple === true)
    return (
      <MultiSelect
        {...sharedProps}
        value={selectedValues.filter((key) => key !== null)}
        onChange={(keys) => props.onChange(updateSelection(keys))}
        style={{ flex: 1 }}
      />
    );

  return (
    <Select
      {...sharedProps}
      value={selectedValues[0] ?? null}
      onChange={(key) => {
        props.onChange(updateSelection(key ? [key] : [])[0] ?? '');
      }}
    />
  );
};
